// ─────────────────────────────────────────────
//  timelineDrag — the arithmetic behind dragging a case bar's edge.
//
//  PURE. No React, no Firestore, no DOM. **The caller passes the rect in.**
//  jsdom has no layout: `getBoundingClientRect()` returns all zeros and there is
//  no ResizeObserver, so anything that measures for itself is untestable without
//  a real browser. Every function here takes the measurement as an argument,
//  which is why the whole gesture's maths can be proved with plain numbers.
//
//  ⚠️ GEOMETRY IS THE INVERSE OF `barMetrics` (timeline.js:501). That helper
//  turns minutes into a PERCENT of the axis; this file turns a pointer x back
//  into minutes. Percent, never pixels — wall mode applies a CSS
//  `transform: scale()` (0.7–2.2) and every stored pixel constant would be
//  silently scaled by it. `clientX` and `rect` are both post-transform, so their
//  RATIO is the only quantity that survives the zoom.
//
//  ⚠️ ONE TIME VOCABULARY. `hmToMinutes` / `minutesToHm` are imported from
//  `domain/timeline`, never re-implemented. This repo has been bitten before by
//  a second copy of a formatter drifting from the first.
// ─────────────────────────────────────────────

import { hmToMinutes, minutesToHm } from "./timeline";

// 15 minutes, and not negotiable. `START_TIME_OPTIONS`/`END_TIME_OPTIONS`
// (components/assignmentRow.cells.js:19-33) are 15-minute slots and `TimeCombo`
// cannot emit a value outside its option list — so a 5-minute drag result would
// be a time the edit modal could not then express or even display as selected.
export const DRAG_STEP_MIN = 15;

// The floor on a dragged case's length.
//
// ⚠️ THIS IS NOT COSMETIC. `isCaselessWindow` (timeline.js:102) reads
// `start === end` as "no window we can honestly draw" and the row becomes a
// standing-watch entry: the bar disappears from the chart entirely and the case
// stops being a case. A drag must therefore never be able to produce an equal
// pair, and clamping at a real minimum is how that is guaranteed rather than
// hoped for.
export const MIN_DURATION_MIN = 15;

const finite = (n) => typeof n === "number" && Number.isFinite(n);

// The pointer x, as unsnapped minutes on the axis.
//
// Returns null when the geometry cannot be trusted — a zero-width rect (jsdom,
// a `display:none` row, a lane that has not laid out yet) or a non-positive
// span. Dividing by that width would yield Infinity/NaN and write a garbage
// time to a live board, so it fails closed and the caller keeps the old value.
//
// The result is CLAMPED to `[axisStart, axisStart + span]`: dragging off the
// left or right of the chart parks the edge at the axis bound rather than
// inventing a time outside the drawn day.
export function minuteAtX({ clientX, rect, axisStart, span } = {}) {
  if (!finite(clientX) || !finite(axisStart) || !finite(span) || !(span > 0)) return null;
  const width = rect && finite(rect.width) ? rect.width : 0;
  const left = rect && finite(rect.left) ? rect.left : 0;
  if (!(width > 0)) return null;

  const frac = Math.max(0, Math.min(1, (clientX - left) / width));
  return axisStart + frac * span;
}

// Nearest `step`-minute slot. Ties round up, matching Math.round.
export function snapMinutes(min, step = DRAG_STEP_MIN) {
  if (!finite(min)) return null;
  const s = finite(step) && step > 0 ? step : DRAG_STEP_MIN;
  return Math.round(min / s) * s;
}

// Apply a candidate minute to ONE edge and return the pair that will be written.
//
// The opposite edge never moves: dragging the start of a case does not push its
// end. Dragging an edge PAST the opposite one clamps to the minimum duration
// rather than inverting the case — an inverted pair is refused downstream by
// `validateCaseTimes` anyway, so inverting would just make the gesture fail.
//
// Guarantees `startMin < endMin` on every path, including the ones that should
// be unreachable. See MIN_DURATION_MIN for why an equal pair is not merely ugly.
export function clampDrag({ edge, candidateMin, startMin, endMin, minDurationMin = MIN_DURATION_MIN } = {}) {
  const s0 = finite(startMin) ? startMin : null;
  const e0 = finite(endMin) ? endMin : null;
  // Nothing sane to clamp against: hand the caller back exactly what it gave us.
  if (s0 == null || e0 == null) return { startMin, endMin };

  const minDur = finite(minDurationMin) && minDurationMin > 0 ? minDurationMin : MIN_DURATION_MIN;
  if (!finite(candidateMin) || (edge !== "start" && edge !== "end")) {
    return { startMin: s0, endMin: e0 };
  }

  let s = s0;
  let e = e0;
  if (edge === "start") {
    s = Math.min(candidateMin, e0 - minDur);
    if (s < 0) s = 0;
  } else {
    e = Math.max(candidateMin, s0 + minDur);
  }

  // Belt and braces. If any branch above ever produced a non-window (a case
  // whose end had already crept below `minDur`, a future edit that forgets the
  // clamp), the bar would vanish from the chart instead of failing loudly.
  // Reachable only when the pair handed in was ALREADY not a window (a row whose
  // end had crept to or below its start). Pushing the end out is the one repair
  // that always works — pulling the start back can run past midnight.
  if (!(s < e)) {
    s = Math.max(0, s);
    e = s + minDur;
  }

  return { startMin: s, endMin: e };
}

// The whole gesture, from a pointer x to the strings that get written.
//
// @param edge        "start" | "end" — which handle is being dragged
// @param clientX     the pointer's x, post-transform
// @param rect        { left, width } of the ROOM'S OWN TRACK node (not the chart:
//                    the lane does not begin at the label edge — the row buttons
//                    and chart padding sit in between)
// @param axisStart   frozen at pointerdown; the axis is derived from the data and
//                    re-derives mid-drag otherwise
// @param span        frozen at pointerdown, same reason
// @param startMin    the case's current start, in minutes
// @param endMin      the case's current end, in minutes
// @param startTime   optional "HH:MM" fallback when startMin is absent/garbage
// @param endTime     optional "HH:MM" fallback when endMin is absent/garbage
//
// @returns { startMin, endMin, startTime, endTime, changed }
//          `startTime`/`endTime` are "HH:MM" 24-hour strings, ready to write to
//          the assignment doc. `startMin`/`endMin` are DERIVED and must never be
//          written back. `changed: false` means "do not write" — the returned
//          times are then the unchanged current ones.
export function dragResult({
  edge,
  clientX,
  rect,
  axisStart,
  span,
  startMin,
  endMin,
  startTime,
  endTime,
  step = DRAG_STEP_MIN,
  minDurationMin = MIN_DURATION_MIN,
} = {}) {
  const s0 = finite(startMin) ? startMin : hmToMinutes(startTime);
  const e0 = finite(endMin) ? endMin : hmToMinutes(endTime);

  const unchanged = () => ({
    startMin: s0 == null ? null : s0,
    endMin: e0 == null ? null : e0,
    startTime: s0 == null ? "" : minutesToHm(s0),
    endTime: e0 == null ? "" : minutesToHm(e0),
    changed: false,
  });

  // A row we cannot read (a null or malformed time), or one that is already not
  // a window. Both mean the bar's pixels do not correspond to its data — the
  // on-call pending bar being the canonical case — so a drag would write a
  // number invented from geometry. Fail closed.
  if (s0 == null || e0 == null || !(e0 > s0)) return unchanged();

  const raw = minuteAtX({ clientX, rect, axisStart, span });
  if (raw == null) return unchanged(); // zero-width rect / unusable axis

  let candidate = snapMinutes(raw, step);
  // Snapping can push a value one step past the axis bound it was clamped to
  // (an axis that does not land on a step boundary). The axis clamp is the hard
  // requirement, so it wins over the snap.
  candidate = Math.max(axisStart, Math.min(axisStart + span, candidate));

  const next = clampDrag({ edge, candidateMin: candidate, startMin: s0, endMin: e0, minDurationMin });
  const changed = next.startMin !== s0 || next.endMin !== e0;

  return {
    startMin: next.startMin,
    endMin: next.endMin,
    startTime: minutesToHm(next.startMin),
    endTime: minutesToHm(next.endMin),
    changed,
  };
}
